import { t } from '@lingui/core/macro'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemTitle,
  ItemTrigger,
} from 'designSystem/components/Item'
import { Text } from 'designSystem/components/Text'
import type { MetadataEditScope } from 'features/knowledgeDocumentViewer/types/metadataEditScope'
import React from 'react'

export type ScopeSummaryCardProps = {
  icon: React.ReactNode
  title: string
  description: string
  disabled?: boolean
  onSelect?: () => void
  action: React.ReactNode
}

export function ScopeSummaryCard({
  icon,
  title,
  description,
  disabled = false,
  onSelect,
  action,
}: ScopeSummaryCardProps): React.ReactElement {
  const mediaAndContent = (
    <>
      <ItemMedia variant='icon' aria-hidden>
        {icon}
      </ItemMedia>
      <ItemContent>
        <ItemTitle>{title}</ItemTitle>
        <Text variant='caption' color='muted'>
          {description}
        </Text>
      </ItemContent>
    </>
  )

  return (
    <Item
      variant={onSelect !== undefined ? 'surface' : 'surfaceMuted'}
      size='lg'
      disabled={disabled}>
      {onSelect !== undefined ? (
        <ItemTrigger type='button' disabled={disabled} onClick={onSelect}>
          {mediaAndContent}
          <ItemActions variant='trailing' aria-hidden>
            {action}
          </ItemActions>
        </ItemTrigger>
      ) : (
        <>
          {mediaAndContent}
          <ItemActions variant='trailing'>{action}</ItemActions>
        </>
      )}
    </Item>
  )
}

export function getScopeChoiceCopy({
  scope,
  familySize,
  metadataSurface,
  currentFilename,
  familyScopeDisabled = false,
}: {
  scope: MetadataEditScope
  familySize: number
  metadataSurface: 'page' | 'document'
  currentFilename?: string
  familyScopeDisabled?: boolean
}): { title: string; description: string } {
  const isDocumentSurface = metadataSurface === 'document'

  if (scope === 'family') {
    return {
      title: t`Edit all ${familySize} revisions`,
      description: familyScopeDisabled
        ? t`Loading revision family edits…`
        : isDocumentSurface
          ? t`Document data changes apply to every document in this revision family.`
          : t`Page data changes apply to every document in this revision family.`,
    }
  }

  const trimmedFilename = currentFilename?.trim()
  if (trimmedFilename) {
    return {
      title: t`Edit just this document`,
      description: isDocumentSurface
        ? t`Document data changes apply only to ${trimmedFilename}.`
        : t`Page data changes apply only to ${trimmedFilename}.`,
    }
  }
  return {
    title: t`Edit just this document`,
    description: isDocumentSurface
      ? t`Document data changes apply only to this document.`
      : t`Page data changes apply only to this document.`,
  }
}
